import { FC, useState } from 'react';
import create from 'zustand';
import { useModalSaving } from 'hooks/useModalSaving';
import clsx from 'clsx';

// components
import Modal from 'components/Modal';
import OrderFlowTimeline, { FlowStep } from 'components/OrderFlowTimeline';

const chipStyles: any = {
  warning: { bg: 'bg-[#FEFECB]', text: 'text-[#7A6B00]', label: 'Confirmation' },
  error:   { bg: 'bg-red-100',   text: 'text-red-700',   label: 'Warning'       },
  success: { bg: 'bg-[#D6EEE8]', text: 'text-[#0F5342]', label: 'Confirmation'  }
};

const ConfirmDialog: FC = () => {
  const { flowStep, flowDirection, message, onSubmit, close, open, variant = 'error' } = useConfirmDialogStore();
  const { setIsSaving } = useModalSaving();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    setIsSaving(false);
    try {
      await onSubmit(true);
      setIsLoading(false);
      setIsSaving(false);
      close();
    } catch (err) {
      setIsLoading(false);
      setIsSaving(false);
    }
  };

  const chip = chipStyles[variant] ?? chipStyles.error;

  return (
    <Modal
      open={open}
      onClose={close}
      classNameTitle="bg-white text-[#29251f] font-Sohne-Regular"
      isConfirmOpen
      size="sm"
    >
      <div className="space-y-4 p-1">
        {flowStep && <OrderFlowTimeline targetStep={flowStep} direction={flowDirection} />}

        <div className="space-y-3">
          <span className={clsx('font-mono uppercase text-xxs px-2 py-0.5 rounded tracking-wide inline-block', chip.bg, chip.text)}>
            {chip.label}
          </span>
          <p className="text-xs text-[#6B6560] leading-relaxed">{message}</p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            disabled={isLoading}
            onClick={close}
            className="text-xs px-5 py-1.5 rounded-full bg-[#F0EDE8] text-[#6B6560] hover:bg-[#e8e4de] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={handleConfirm}
            className={clsx(
              'text-xs px-5 py-1.5 rounded-full bg-[#FFFDA5] text-black hover:bg-[#FFF799] transition-colors',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isLoading ? 'Confirming…' : 'Confirm'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

interface IConfirm {
  title: string;
  subtitle?: string;
  flowStep?: FlowStep;
  flowDirection?: 'forward' | 'backward';
  message: string;
  variant?: 'error' | 'success' | 'warning';
  onSubmit: (x: boolean) => Promise<any> | void;
}

const useConfirmDialogStore = create<IConfirm & { close: () => void; open: boolean }>((set) => ({
  title: '',
  subtitle: undefined,
  flowStep: undefined,
  flowDirection: undefined,
  message: '',
  open: false,
  variant: 'error',
  onSubmit: (open: boolean) => set({ open }),
  close: () => set({ open: false })
}));

export const confirmDialog = ({ title, subtitle, flowStep, flowDirection, message, variant = 'error', onSubmit }: IConfirm) => {
  useConfirmDialogStore.setState({ title, subtitle, flowStep, flowDirection, open: true, variant, message, onSubmit });
};

export default ConfirmDialog;
